import React from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';

/**
 * Full Bleed layout — poster/billboard style.
 * Massive title takes up most of the slide. Minimal body.
 * Footer pinned at bottom for cross-template consistency.
 */
export function FullBleedLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const titleSize = Math.max(
    TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74,
    96
  );

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  const handleBodyClick = (e) => {
    if (!interactive || !onElementClick) return;
    e.stopPropagation();
    onElementClick('text', 'subtitle');
  };

  return (
    <>
      {/* Content area */}
      <NudgeableGroup groupId="content" className="absolute inset-[56px] bottom-[140px] flex flex-col justify-end">
        {/* Giant title — inline editable */}
        <div className="flex-1 flex items-end pb-6">
          <InlineEditable
            value={data.title}
            isEditing={editingField === 'title'}
            onChange={(html) => onEditChange && onEditChange('title', html)}
            onBlur={onEndEdit}
            className={`leading-[0.95] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
            style={{
              fontSize: `${titleSize * 1.3}px`,
              fontFamily: fontPreset.title,
              fontWeight: fontPreset.titleWeight,
              color: pc.el.title,
            }}
            onClick={handleTitleClick}
          >
            {renderFormattedText(data.title)}
          </InlineEditable>
        </div>

        {/* Thin separator */}
        <div className="w-full h-[2px] mb-6" style={{ backgroundColor: pc.text(0.15) }} />

        {/* Compact body row */}
        <div className="min-w-0">
          {data.subtitleEnabled !== false && data.subtitle ? (
            <InlineEditable
              value={data.subtitle || ''}
              isEditing={editingField === 'subtitle'}
              onChange={(text) => onEditChange && onEditChange('subtitle', text)}
              onBlur={onEndEdit}
              plainText
              className={`text-[28px] leading-snug${editingField === 'subtitle' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
              style={{ fontFamily: fontPreset.subtitle, color: pc.el.subtitle }}
              onClick={handleBodyClick}
            >
              {data.subtitle}
            </InlineEditable>
          ) : data.bodyEnabled !== false && data.body ? (
            <InlineEditable
              value={data.body || ''}
              isEditing={editingField === 'body'}
              onChange={(html) => onEditChange && onEditChange('body', html)}
              onBlur={onEndEdit}
              className={`leading-snug line-clamp-3${editingField === 'body' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
              style={{ fontFamily: fontPreset.body, color: pc.el.body, fontSize: `${BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32}px` }}
              onClick={handleBodyClick}
            >
              {renderFormattedText(data.body)}
            </InlineEditable>
          ) : null}
        </div>
      </NudgeableGroup>

      {/* Footer — pinned at bottom, positioned relative to the slide */}
      <div className="absolute bottom-[64px] left-[64px] right-[64px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
